<template>
  <!-- Show loading screen while app is initializing -->
  <transition name="fade">
    <loading-screen v-if="!appFinalized" :message="$t(bootMessage)" />
  </transition>

  <!-- Show error state if initialization failed -->
  <error-screen
    v-if="appFinalized && appInitialError"
    :title="$t('boot.not_loaded_correctly')"
    :message="$t('boot.connection_message')"
    :action-label="$t('boot.reload_page')"
    :secondary-action-label="$t('boot.logout_and_retry')"
    @action="reload"
    @secondary-action="logoutAndReload"
  />

  <!-- Show main app once initialized -->
  <template v-else>
    <toast-container />
    <app-review-modal
      :visible="reviewModalVisible"
      @accept="acceptReview"
      @dismiss="dismissReview"
    />

    <div
      class="mt-0 min-h-screen w-full bg-kekkle-yellow select-none dark:bg-kekkle-dark-dark"
    >
      <div class="relative mx-auto max-w-xl">
        <suspense>
          <router-view />
        </suspense>
      </div>
    </div>
  </template>
</template>

<script setup>
import { registerSW } from "virtual:pwa-register";
import { computed } from "vue";
import { useAppStore } from "@/stores/modules/app/app";
import ErrorScreen from "@/components/ErrorScreen.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import ToastContainer from "@/components/ToastContainer.vue";
import AppReviewModal from "@/components/Modals/AppReviewModal.vue";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { setErrorToast, setInfoToast } from "./helpers/notifications/toast";

const appStore = useAppStore();
const authStore = useAuthStore();
const { reviewModalVisible, dismissReview, acceptReview } =
  useAppReviewPrompt();

if ("serviceWorker" in navigator) {
  try {
    registerSW({ immediate: true });
  } catch {
    //
  }
}

function reload() {
  window.location.reload();
}

async function logoutAndReload() {
  await authStore.logout();
  window.location.reload();
}

const appFinalized = computed(() => appStore.finalized);
const appInitialError = computed(() => appStore.initialError);
const bootMessage = computed(() => appStore.bootMessage);
</script>

<style>
.fade-enter-active {
  transition: all 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-leave-active {
  transition: all 900ms cubic-bezier(0.87, 0, 0.13, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(1) translateY(0);
  filter: blur(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(1.1);
  filter: blur(25px) brightness(1.3);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
  filter: blur(0) brightness(1);
}

.firebase-emulator-warning {
  display: none;
}
</style>
